
import {
  type Automation,
  type AutomationRun,
  createAutomation,
  deleteAutomation,
  listAutomationRuns,
  listAutomations,
  runAutomation,
  setAutomationEnabled,
} from "../api/automations.js";
import { listEmailAccounts, type EmailAccount } from "../api/settings.js";
import { automationStyles } from "./app-automations-styles.js";

type RunView = {
  id: string;
  status: string;
  statusLabel: string;
  startedAt: number;
  finishedAt: number | null;
  startedLabel: string;
  finishedLabel: string;
  output: string;
  outputText: string;
};

type AutomationItem = Automation & {
  kindLabel: string;
  nameLabel: string;
  notifyLabel: string;
  triggerLabel: string;
  lastRunLabel: string;
};

type AutomationsHost = HTMLElement & {
  items: AutomationItem[];
  loading: boolean;
  error: string;
  creating: boolean;
  formError: string;
  selectedId: string;
  selectedName: string;
  runs: RunView[];
  runsLoading: boolean;
  webhookOpen: boolean;
  webhookUrl: string;
  webhookSecret: string;
  emailAccounts: EmailAccount[];
  createTrigger: string;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDate(seconds: number | null): string {
  if (!seconds) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(seconds * 1000));
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function describeTrigger(item: Automation, accounts: EmailAccount[]): string {
  if (item.trigger_kind === "cron") return item.schedule || "Cron schedule";
  if (item.trigger_kind === "email") {
    const accountId = String(item.trigger_config?.account_id ?? "");
    const account = accounts.find((candidate) => candidate.id === accountId);
    return account ? `New mail in ${account.name}` : "New incoming email";
  }
  if (item.trigger_kind === "gmail") return "New Gmail";
  if (item.trigger_kind === "webhook") return "Webhook";
  if (item.trigger_kind === "vfs_change") return "File change";
  return "Manual";
}

function describeLastRun(item: Automation): string {
  return item.last_run ? `Last run ${formatDate(item.last_run)}` : "Never run";
}

function toAutomationItem(item: Automation, accounts: EmailAccount[]): AutomationItem {
  return {
    ...item,
    kindLabel: titleCase(item.kind),
    nameLabel: escapeHtml(item.name),
    notifyLabel: item.notify_kind !== "none" ? `• Notify ${titleCase(item.notify_kind)}` : "",
    triggerLabel: escapeHtml(describeTrigger(item, accounts)),
    lastRunLabel: describeLastRun(item),
  };
}

function emailAccountList(accounts: EmailAccount[] | unknown): EmailAccount[] {
  return Array.isArray(accounts) ? accounts : [];
}

function reportRunError(host: AutomationsHost, error: unknown): void {
  host.error = error instanceof Error ? error.message : "Failed to start run.";
}

async function load(host: AutomationsHost): Promise<void> {
  host.loading = true;
  host.error = "";
  try {
    host.items = (await listAutomations()).map((item) => toAutomationItem(item, emailAccountList(host.emailAccounts)));
    if (!host.selectedId && host.items.length > 0) {
      await selectAutomation(host, host.items[0]);
    } else if (host.selectedId && !host.items.some((item) => item.id === host.selectedId)) {
      host.selectedId = "";
      host.selectedName = "";
      host.runs = [];
    }
  } catch {
    host.error = "Failed to load automations.";
  } finally {
    host.loading = false;
  }
}

async function selectAutomation(host: AutomationsHost, item: Automation, clearRuns = true): Promise<void> {
  host.selectedId = item.id;
  host.selectedName = item.name;
  host.runsLoading = true;
  if (clearRuns) {
    host.runs = [];
  }
  try {
    const runs = await listAutomationRuns(item.id);
    host.runs = runs.map((run: AutomationRun): RunView => {
      const outputText = run.output.trim();
      return {
        id: run.id,
        status: run.status,
        statusLabel: titleCase(run.status),
        startedAt: run.started_at,
        finishedAt: run.finished_at,
        startedLabel: formatDate(run.started_at),
        finishedLabel: run.finished_at ? formatDate(run.finished_at) : "Still running",
        output: escapeHtml(outputText || "Run completed, but it did not produce text output."),
        outputText,
      };
    });
  } catch {
    host.runs = [];
    host.error = "Failed to load automation runs.";
  } finally {
    host.runsLoading = false;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function runAndRefresh(host: AutomationsHost, item: Automation): Promise<void> {
  host.error = "";
  if (host.selectedId !== item.id) {
    await selectAutomation(host, item);
  }

  const requestedAt = Math.floor(Date.now() / 1000);
  const previousLatestRunId = host.runs[0]?.id ?? "";
  const pendingRun: RunView = {
    id: `pending-${Date.now()}`,
    status: "running",
    statusLabel: "Running",
    startedAt: requestedAt,
    finishedAt: null,
    startedLabel: "Starting now",
    finishedLabel: "Still running",
    output: "Run request accepted. Waiting for scheduler output…",
    outputText: "",
  };

  host.selectedId = item.id;
  host.selectedName = item.name;
  host.runs = [pendingRun, ...host.runs];

  await runAutomation(item.id);

  for (let attempt = 0; attempt < 16; attempt += 1) {
    await delay(attempt < 4 ? 500 : 1000);
    await selectAutomation(host, item, false);
    const latest = host.runs[0];
    const isRequestedRun = latest && latest.id !== previousLatestRunId && latest.startedAt >= requestedAt - 1;
    const hasFinishedWithOutput = isRequestedRun && latest.finishedAt !== null && latest.outputText.length > 0;
    const hasSettledWithoutOutput = isRequestedRun && latest.finishedAt !== null && attempt === 15;
    if (hasFinishedWithOutput || hasSettledWithoutOutput) {
      break;
    }
  }

  await load(host);
  await selectAutomation(host, item, false);
}

function triggerKindFromForm(value: string): Automation["trigger_kind"] {
  if (value === "webhook") return "webhook";
  if (value === "manual") return "manual";
  if (value === "vfs_change") return "vfs_change";
  if (value === "email") return "email";
  if (value === "gmail") return "gmail";
  return "cron";
}

function submitCreateAutomation(host: AutomationsHost, event: Event): void {
  event.preventDefault();
  const data = new FormData(event.currentTarget as HTMLFormElement);
  host.formError = "";
  const triggerKind = triggerKindFromForm(String(data.get("trigger") ?? "cron"));
  const notify = String(data.get("notify") ?? "none");
  void createAutomation({
    name: String(data.get("name") ?? "").trim(),
    schedule: String(data.get("schedule") ?? "").trim(),
    kind: data.get("kind") === "python" ? "python" : "agent",
    payload: String(data.get("payload") ?? ""),
    enabled: data.get("enabled") !== null,
    trigger_kind: triggerKind,
    notify_kind: notify === "telegram" ? "telegram" : "none",
    ...(triggerKind === "vfs_change"
      ? { trigger_config: { path: String(data.get("watch_path") ?? "").trim() } }
      : triggerKind === "email"
        ? { trigger_config: { account_id: String(data.get("email_account") ?? "") } }
        : {}),
  })
    .then(async (created) => {
      host.creating = false;
      if (created.webhook_secret) {
        host.webhookUrl = `${location.origin}/api/automations/${created.id}/webhook`;
        host.webhookSecret = created.webhook_secret;
        host.webhookOpen = true;
      }
      await load(host);
      await selectAutomation(host, created);
    })
    .catch((err: unknown) => {
      host.formError =
        err instanceof Error && err.message === "400"
          ? "Check the name, cron schedule, and task."
          : "Failed to create automation.";
    });
}

export class AppAutomations extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppAutomations)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["items","data-items","loading","data-loading","error","data-error","creating","data-creating","formerror","data-form-error","selectedid","data-selected-id","selectedname","data-selected-name","runs","data-runs","runsloading","data-runs-loading","webhookopen","data-webhook-open","webhookurl","data-webhook-url","webhooksecret","data-webhook-secret","emailaccounts","data-email-accounts","createtrigger","data-create-trigger"];

  #items = [];
  #loading = false;
  #error = "";
  #creating = false;
  #formError = "";
  #selectedId = "";
  #selectedName = "";
  #runs = [];
  #runsLoading = false;
  #webhookOpen = false;
  #webhookUrl = "";
  #webhookSecret = "";
  #emailAccounts = [];
  #createTrigger = "cron";
  #itemsSet = false;
  #loadingSet = false;
  #errorSet = false;
  #creatingSet = false;
  #formErrorSet = false;
  #selectedIdSet = false;
  #selectedNameSet = false;
  #runsSet = false;
  #runsLoadingSet = false;
  #webhookOpenSet = false;
  #webhookUrlSet = false;
  #webhookSecretSet = false;
  #emailAccountsSet = false;
  #createTriggerSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get items() {
    return this.#items;
  }

  set items(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#items, next)) return;
    this.#items = next;
    this.#itemsSet = true;
    this.#updateBindings('items');
  }

  get loading() {
    return this.#loading;
  }

  set loading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#loading, next)) return;
    this.#loading = next;
    this.#loadingSet = true;
    this.#updateBindings('loading');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  get creating() {
    return this.#creating;
  }

  set creating(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#creating, next)) return;
    this.#creating = next;
    this.#creatingSet = true;
    this.#updateBindings('creating');
  }

  get formError() {
    return this.#formError;
  }

  set formError(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#formError, next)) return;
    this.#formError = next;
    this.#formErrorSet = true;
    this.#updateBindings('formError');
  }

  get selectedId() {
    return this.#selectedId;
  }

  set selectedId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedId, next)) return;
    this.#selectedId = next;
    this.#selectedIdSet = true;
    this.#updateBindings('selectedId');
  }

  get selectedName() {
    return this.#selectedName;
  }

  set selectedName(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedName, next)) return;
    this.#selectedName = next;
    this.#selectedNameSet = true;
    this.#updateBindings('selectedName');
  }

  get runs() {
    return this.#runs;
  }

  set runs(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#runs, next)) return;
    this.#runs = next;
    this.#runsSet = true;
    this.#updateBindings('runs');
  }

  get runsLoading() {
    return this.#runsLoading;
  }

  set runsLoading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#runsLoading, next)) return;
    this.#runsLoading = next;
    this.#runsLoadingSet = true;
    this.#updateBindings('runsLoading');
  }

  get webhookOpen() {
    return this.#webhookOpen;
  }

  set webhookOpen(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#webhookOpen, next)) return;
    this.#webhookOpen = next;
    this.#webhookOpenSet = true;
    this.#updateBindings('webhookOpen');
  }

  get webhookUrl() {
    return this.#webhookUrl;
  }

  set webhookUrl(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#webhookUrl, next)) return;
    this.#webhookUrl = next;
    this.#webhookUrlSet = true;
    this.#updateBindings('webhookUrl');
  }

  get webhookSecret() {
    return this.#webhookSecret;
  }

  set webhookSecret(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#webhookSecret, next)) return;
    this.#webhookSecret = next;
    this.#webhookSecretSet = true;
    this.#updateBindings('webhookSecret');
  }

  get emailAccounts() {
    return this.#emailAccounts;
  }

  set emailAccounts(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#emailAccounts, next)) return;
    this.#emailAccounts = next;
    this.#emailAccountsSet = true;
    this.#updateBindings('emailAccounts');
  }

  get createTrigger() {
    return this.#createTrigger;
  }

  set createTrigger(value) {
    const raw = value ?? "cron";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#createTrigger, next)) return;
    this.#createTrigger = next;
    this.#createTriggerSet = true;
    this.#updateBindings('createTrigger');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-items')) this.items = this.dataset.items;
    if (this.hasAttribute('data-loading')) this.loading = this.dataset.loading;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
    if (this.hasAttribute('data-creating')) this.creating = this.dataset.creating;
    if (this.hasAttribute('data-form-error')) this.formError = this.dataset.formError;
    if (this.hasAttribute('data-selected-id')) this.selectedId = this.dataset.selectedId;
    if (this.hasAttribute('data-selected-name')) this.selectedName = this.dataset.selectedName;
    if (this.hasAttribute('data-runs')) this.runs = this.dataset.runs;
    if (this.hasAttribute('data-runs-loading')) this.runsLoading = this.dataset.runsLoading;
    if (this.hasAttribute('data-webhook-open')) this.webhookOpen = this.dataset.webhookOpen;
    if (this.hasAttribute('data-webhook-url')) this.webhookUrl = this.dataset.webhookUrl;
    if (this.hasAttribute('data-webhook-secret')) this.webhookSecret = this.dataset.webhookSecret;
    if (this.hasAttribute('data-email-accounts')) this.emailAccounts = this.dataset.emailAccounts;
    if (this.hasAttribute('data-create-trigger')) this.createTrigger = this.dataset.createTrigger;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.items = props.items;
      this.loading = props.loading;
      this.error = props.error;
      this.creating = props.creating;
      this.formError = props.formError;
      this.selectedId = props.selectedId;
      this.selectedName = props.selectedName;
      this.runs = props.runs;
      this.runsLoading = props.runsLoading;
      this.webhookOpen = props.webhookOpen;
      this.webhookUrl = props.webhookUrl;
      this.webhookSecret = props.webhookSecret;
      this.emailAccounts = props.emailAccounts;
      this.createTrigger = props.createTrigger;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#itemsSet) this.items = this.dataset.items;
    if (!this.#loadingSet) this.loading = this.dataset.loading;
    if (!this.#errorSet) this.error = this.dataset.error;
    if (!this.#creatingSet) this.creating = this.dataset.creating;
    if (!this.#formErrorSet) this.formError = this.dataset.formError;
    if (!this.#selectedIdSet) this.selectedId = this.dataset.selectedId;
    if (!this.#selectedNameSet) this.selectedName = this.dataset.selectedName;
    if (!this.#runsSet) this.runs = this.dataset.runs;
    if (!this.#runsLoadingSet) this.runsLoading = this.dataset.runsLoading;
    if (!this.#webhookOpenSet) this.webhookOpen = this.dataset.webhookOpen;
    if (!this.#webhookUrlSet) this.webhookUrl = this.dataset.webhookUrl;
    if (!this.#webhookSecretSet) this.webhookSecret = this.dataset.webhookSecret;
    if (!this.#emailAccountsSet) this.emailAccounts = this.dataset.emailAccounts;
    if (!this.#createTriggerSet) this.createTrigger = this.dataset.createTrigger;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void listEmailAccounts()
        .then((accounts) => { this.emailAccounts = accounts; })
        .catch(() => { this.emailAccounts = []; })
        .then(() => load(this));
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'items':
      case 'data-items':
        this.items = newValue;
        break;
      case 'loading':
      case 'data-loading':
        this.loading = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
      case 'creating':
      case 'data-creating':
        this.creating = newValue;
        break;
      case 'formerror':
      case 'data-form-error':
        this.formError = newValue;
        break;
      case 'selectedid':
      case 'data-selected-id':
        this.selectedId = newValue;
        break;
      case 'selectedname':
      case 'data-selected-name':
        this.selectedName = newValue;
        break;
      case 'runs':
      case 'data-runs':
        this.runs = newValue;
        break;
      case 'runsloading':
      case 'data-runs-loading':
        this.runsLoading = newValue;
        break;
      case 'webhookopen':
      case 'data-webhook-open':
        this.webhookOpen = newValue;
        break;
      case 'webhookurl':
      case 'data-webhook-url':
        this.webhookUrl = newValue;
        break;
      case 'webhooksecret':
      case 'data-webhook-secret':
        this.webhookSecret = newValue;
        break;
      case 'emailaccounts':
      case 'data-email-accounts':
        this.emailAccounts = newValue;
        break;
      case 'createtrigger':
      case 'data-create-trigger':
        this.createTrigger = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const safeEmailAccounts = emailAccountList(this.#emailAccounts);
    const viewItems = this.#items.map((item) => item.nameLabel === undefined ? toAutomationItem(item, safeEmailAccounts) : item);
    const emailAccountViews = safeEmailAccounts.map((account) => ({
    id: account.id,
    label: escapeHtml(`${account.name} — ${account.email}`),
}));
    const activeCount = viewItems.filter((item) => item.enabled).length;
    const lastRunCount = viewItems.filter((item) => item.last_run).length;
    return `<style>${automationStyles}</style><div class="root"><div class="content"><header class="hero"><div><p class="eyebrow">Operations</p><h1>Automations</h1><p class="muted">Schedule recurring work, expose webhook tasks, react to file changes, and inspect every run output in one place.</p></div><button class="primary" type="button" data-argon="10019">New automation</button></header><section class="stats" aria-label="Automation summary"><div class="stat"><span>Total</span><strong><!--argon-text:0-->${viewItems.length}<!--/argon-text:0--></strong></div><div class="stat"><span>Enabled</span><strong><!--argon-text:1-->${activeCount}<!--/argon-text:1--></strong></div><div class="stat"><span>With runs</span><strong><!--argon-text:2-->${lastRunCount}<!--/argon-text:2--></strong></div></section><main class="workspace"><section class="panel"><div class="panel-head"><h2>Automations</h2><span class="badge"><!--argon-text:3-->${this.#escText(this.#loading ? "Loading" : `${viewItems.length} total`)}<!--/argon-text:3--></span></div><div class="panel-body"><!--argon-text:4-->${(viewItems.length === 0) ? `<div class="empty"><strong>${this.#escText(this.#loading ? "Loading automations…" : "No automations yet")}</strong><span>Create one to run tasks on a schedule, webhook, file change, or manually.</span></div>` : `<div class="automation-list">${viewItems.map((item, __argonIndex) => this.#mapEventItem(`<div class="${this.#escAttr(`automation-card ${(item.id === this.#selectedId) ? "selected" : ""}`)}" data-notify-kind="${item.notify_kind}"><button class="ghost" type="button" aria-label="${this.#escAttr(`Select ${item.name}`)}" data-argon="11652"><div><div class="name-row"><span class="name">${item.nameLabel || item.name}</span><span class="${this.#escAttr(`badge ${item.enabled ? "on" : "off"}`)}">${this.#escText(item.enabled ? "Enabled" : "Paused")}</span></div><div class="meta"><span>${item.kindLabel || item.kind}</span><span>•</span><span>${(item.triggerLabel || item.schedule) || item.trigger_kind}</span><span>•</span><span>${item.lastRunLabel || "Never run"}</span>${item.notifyLabel ? `<span>${item.notifyLabel}</span>` : (item.notify_kind !== "none") ? `<span>${item.notify_kind}</span>` : this.#escText("")}</div></div></button><div class="row-actions"><button type="button" aria-label="${this.#escAttr(`Run ${item.name}`)}" data-argon="12922">Run</button><button type="button" aria-label="${this.#escAttr(`${item.enabled ? "Pause" : "Enable"} ${item.name}`)}" data-argon="13319">${this.#escText(item.enabled ? "On" : "Off")}</button><button class="danger" type="button" aria-label="${this.#escAttr(`Delete ${item.name}`)}" data-argon="13997">Delete</button></div></div>`, "initial:5:11652.12922.13319.13997:" + __argonIndex, { 11652: () => { void selectAutomation(this, item); }, 12922: () => { void runAndRefresh(this, item).catch((runError) => reportRunError(this, runError)); }, 13319: () => {
    void setAutomationEnabled(item.id, !item.enabled)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to update automation.";
    });
}, 13997: () => {
    if (!window.confirm(`Delete automation "${item.name}"?`))
        return;
    void deleteAutomation(item.id)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to delete automation.";
    });
} }, ["11652", "12922", "13319", "13997"])).join("")}</div>`}<!--/argon-text:4--></div></section><section class="panel"><div class="panel-head"><h2><!--argon-text:5-->${this.#escText(this.#selectedName || "Run output")}<!--/argon-text:5--></h2><span class="badge">History</span></div><div class="panel-body"><!--argon-text:6-->${(!this.#selectedId) ? "<div class=\"empty\"><strong>Select an automation</strong><span>Run logs and captured output will appear here.</span></div>" : (this.#runs.length === 0) ? `<div class="empty"><strong>${this.#escText(this.#runsLoading ? "Loading runs…" : "No executions yet")}</strong><span>${this.#escText(this.#runsLoading ? "Fetching the latest run history." : "Click Run to start it and capture output.")}</span></div>` : `<div class="run-list">${this.#runs.map((run, index) => `<details class="run"${(index === 0) ? ' open' : ''}><summary><span class="${this.#escAttr(`badge ${run.status}`)}">${run.statusLabel}</span><span>${run.startedLabel}</span><span class="run-meta">Finished ${run.finishedLabel}</span></summary><pre>${run.output}</pre></details>`).join("")}</div>`}<!--/argon-text:6--></div></section></main><div class="error"><!--argon-text:7-->${this.#escText(this.#error)}<!--/argon-text:7--></div></div><!--argon-text:8-->${this.#creating ? `<div class="modal" data-argon="16343"><form class="modal-card" data-argon="16516"><div class="modal-title"><div><h2>New automation</h2><p class="muted">Define when it runs, what it does, and where notifications go.</p></div><button type="button" data-argon="16853">Close</button></div><div class="form-grid"><label>Name<input name="name" required placeholder="Daily report"></label><label>Trigger<select name="trigger" data-argon="17117"><option value="cron">Cron schedule</option><option value="email">Incoming email</option><option value="gmail">Incoming Gmail</option><option value="webhook">Webhook (HTTP)</option><option value="vfs_change">File change</option><option value="manual">Manual only</option></select></label>${(this.#createTrigger === "cron") ? "<label>Schedule<input name=\"schedule\" required placeholder=\"*/30 * * * *\"><span class=\"hint\">Standard five-field cron expression in UTC.</span></label>" : "<input name=\"schedule\" type=\"hidden\" value=\"\">"}${(this.#createTrigger === "email") ? `<label>Inbox<select name="email_account" required><option value="">Choose an inbox</option>${emailAccountViews.map((account) => `<option value="${account.id}">${account.label}</option>`).join("")}</select><span class="hint">Add IMAP accounts in Settings. Existing mail is ignored when the automation is created.</span></label>` : this.#escText("")}${(this.#createTrigger === "gmail") ? "<span class=\"hint\">Fires when new mail arrives in your connected Gmail inbox. Connect Google in Settings first. Existing mail is ignored when the automation is created.</span>" : this.#escText("")}${(this.#createTrigger === "vfs_change") ? "<label>Watch path<input name=\"watch_path\" placeholder=\"reports/ (empty means all files)\"><span class=\"hint\">Leave empty to watch all files.</span></label>" : this.#escText("")}<label>Type<select name="kind"><option value="agent">Agent prompt</option><option value="python">Python script</option></select></label><label>Notify<select name="notify"><option value="none">Store output only</option><option value="telegram">Telegram</option></select></label><label>Task<textarea name="payload" required placeholder="Describe the task or paste Python code..."></textarea></label><label class="inline"><input type="checkbox" name="enabled" checked> Enabled</label></div><div class="actions"><button type="button" data-argon="19208">Cancel</button><button class="primary" type="submit">Create automation</button></div><div class="error">${this.#escText(this.#formError)}</div></form></div>` : this.#escText("")}${this.#webhookOpen ? `<div class="modal" data-argon="19552"><div class="modal-card"><div class="modal-title"><div><h2>Webhook created</h2><p class="muted">Copy the secret now. It is shown only once.</p></div><button type="button" data-argon="19976">Close</button></div><div class="form-grid"><label>URL<input class="secret" value="${this.#escAttr(this.#webhookUrl)}" readonly></label><label>Secret<input class="secret" value="${this.#escAttr(this.#webhookSecret)}" readonly></label><p class="muted">Send a POST request with <code>X-Stride-Webhook-Secret</code> or <code>?token=</code>. JSON bodies are forwarded to the task.</p></div></div></div>` : this.#escText("")}<!--/argon-text:8--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const safeEmailAccounts = emailAccountList(this.#emailAccounts);
    const viewItems = this.#items.map((item) => item.nameLabel === undefined ? toAutomationItem(item, safeEmailAccounts) : item);
    const emailAccountViews = safeEmailAccounts.map((account) => ({
    id: account.id,
    label: escapeHtml(`${account.name} — ${account.email}`),
}));
    const activeCount = viewItems.filter((item) => item.enabled).length;
    const lastRunCount = viewItems.filter((item) => item.last_run).length;
    if (prop === undefined || prop === 'items' || prop === 'emailAccounts') {
      const __argonNext = `${viewItems.length}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'items' || prop === 'emailAccounts') {
      const __argonNext = `${activeCount}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'items' || prop === 'emailAccounts') {
      const __argonNext = `${lastRunCount}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'loading' || prop === 'items' || prop === 'emailAccounts') {
      const __argonNext = `${this.#escText(this.#loading ? "Loading" : `${viewItems.length} total`)}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined || prop === 'items' || prop === 'emailAccounts' || prop === 'loading' || prop === 'selectedId') {
      const __argonNext = `${(viewItems.length === 0) ? `<div class="empty"><strong>${this.#escText(this.#loading ? "Loading automations…" : "No automations yet")}</strong><span>Create one to run tasks on a schedule, webhook, file change, or manually.</span></div>` : `<div class="automation-list">${viewItems.map((item, __argonIndex) => this.#mapEventItem(`<div class="${this.#escAttr(`automation-card ${(item.id === this.#selectedId) ? "selected" : ""}`)}" data-notify-kind="${item.notify_kind}"><button class="ghost" type="button" aria-label="${this.#escAttr(`Select ${item.name}`)}" data-argon="11652"><div><div class="name-row"><span class="name">${item.nameLabel || item.name}</span><span class="${this.#escAttr(`badge ${item.enabled ? "on" : "off"}`)}">${this.#escText(item.enabled ? "Enabled" : "Paused")}</span></div><div class="meta"><span>${item.kindLabel || item.kind}</span><span>•</span><span>${(item.triggerLabel || item.schedule) || item.trigger_kind}</span><span>•</span><span>${item.lastRunLabel || "Never run"}</span>${item.notifyLabel ? `<span>${item.notifyLabel}</span>` : (item.notify_kind !== "none") ? `<span>${item.notify_kind}</span>` : this.#escText("")}</div></div></button><div class="row-actions"><button type="button" aria-label="${this.#escAttr(`Run ${item.name}`)}" data-argon="12922">Run</button><button type="button" aria-label="${this.#escAttr(`${item.enabled ? "Pause" : "Enable"} ${item.name}`)}" data-argon="13319">${this.#escText(item.enabled ? "On" : "Off")}</button><button class="danger" type="button" aria-label="${this.#escAttr(`Delete ${item.name}`)}" data-argon="13997">Delete</button></div></div>`, "4:5:11652.12922.13319.13997:" + __argonIndex, { 11652: () => { void selectAutomation(this, item); }, 12922: () => { void runAndRefresh(this, item).catch((runError) => reportRunError(this, runError)); }, 13319: () => {
    void setAutomationEnabled(item.id, !item.enabled)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to update automation.";
    });
}, 13997: () => {
    if (!window.confirm(`Delete automation "${item.name}"?`))
        return;
    void deleteAutomation(item.id)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to delete automation.";
    });
} }, ["11652", "12922", "13319", "13997"])).join("")}</div>`}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined || prop === 'selectedName') {
      const __argonNext = `${this.#escText(this.#selectedName || "Run output")}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'selectedId' || prop === 'runs' || prop === 'runsLoading') {
      const __argonNext = `${(!this.#selectedId) ? "<div class=\"empty\"><strong>Select an automation</strong><span>Run logs and captured output will appear here.</span></div>" : (this.#runs.length === 0) ? `<div class="empty"><strong>${this.#escText(this.#runsLoading ? "Loading runs…" : "No executions yet")}</strong><span>${this.#escText(this.#runsLoading ? "Fetching the latest run history." : "Click Run to start it and capture output.")}</span></div>` : `<div class="run-list">${this.#runs.map((run, index) => `<details class="run"${(index === 0) ? ' open' : ''}><summary><span class="${this.#escAttr(`badge ${run.status}`)}">${run.statusLabel}</span><span>${run.startedLabel}</span><span class="run-meta">Finished ${run.finishedLabel}</span></summary><pre>${run.output}</pre></details>`).join("")}</div>`}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'creating' || prop === 'createTrigger' || prop === 'emailAccounts' || prop === 'formError' || prop === 'webhookOpen' || prop === 'webhookUrl' || prop === 'webhookSecret') {
      const __argonNext = `${this.#creating ? `<div class="modal" data-argon="16343"><form class="modal-card" data-argon="16516"><div class="modal-title"><div><h2>New automation</h2><p class="muted">Define when it runs, what it does, and where notifications go.</p></div><button type="button" data-argon="16853">Close</button></div><div class="form-grid"><label>Name<input name="name" required placeholder="Daily report"></label><label>Trigger<select name="trigger" data-argon="17117"><option value="cron">Cron schedule</option><option value="email">Incoming email</option><option value="gmail">Incoming Gmail</option><option value="webhook">Webhook (HTTP)</option><option value="vfs_change">File change</option><option value="manual">Manual only</option></select></label>${(this.#createTrigger === "cron") ? "<label>Schedule<input name=\"schedule\" required placeholder=\"*/30 * * * *\"><span class=\"hint\">Standard five-field cron expression in UTC.</span></label>" : "<input name=\"schedule\" type=\"hidden\" value=\"\">"}${(this.#createTrigger === "email") ? `<label>Inbox<select name="email_account" required><option value="">Choose an inbox</option>${emailAccountViews.map((account) => `<option value="${account.id}">${account.label}</option>`).join("")}</select><span class="hint">Add IMAP accounts in Settings. Existing mail is ignored when the automation is created.</span></label>` : this.#escText("")}${(this.#createTrigger === "gmail") ? "<span class=\"hint\">Fires when new mail arrives in your connected Gmail inbox. Connect Google in Settings first. Existing mail is ignored when the automation is created.</span>" : this.#escText("")}${(this.#createTrigger === "vfs_change") ? "<label>Watch path<input name=\"watch_path\" placeholder=\"reports/ (empty means all files)\"><span class=\"hint\">Leave empty to watch all files.</span></label>" : this.#escText("")}<label>Type<select name="kind"><option value="agent">Agent prompt</option><option value="python">Python script</option></select></label><label>Notify<select name="notify"><option value="none">Store output only</option><option value="telegram">Telegram</option></select></label><label>Task<textarea name="payload" required placeholder="Describe the task or paste Python code..."></textarea></label><label class="inline"><input type="checkbox" name="enabled" checked> Enabled</label></div><div class="actions"><button type="button" data-argon="19208">Cancel</button><button class="primary" type="submit">Create automation</button></div><div class="error">${this.#escText(this.#formError)}</div></form></div>` : this.#escText("")}${this.#webhookOpen ? `<div class="modal" data-argon="19552"><div class="modal-card"><div class="modal-title"><div><h2>Webhook created</h2><p class="muted">Copy the secret now. It is shown only once.</p></div><button type="button" data-argon="19976">Close</button></div><div class="form-grid"><label>URL<input class="secret" value="${this.#escAttr(this.#webhookUrl)}" readonly></label><label>Secret<input class="secret" value="${this.#escAttr(this.#webhookSecret)}" readonly></label><p class="muted">Send a POST request with <code>X-Stride-Webhook-Secret</code> or <code>?token=</code>. JSON bodies are forwarded to the task.</p></div></div></div>` : this.#escText("")}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '10019').forEach(el => this.#listen(el, '10019', 'click', () => {
    this.createTrigger = "cron";
    this.creating = true;
}));
    this.#eventTargets(scope, '11652').forEach(el => this.#listen(el, '11652', 'click', () => { void selectAutomation(this, item); }));
    this.#eventTargets(scope, '12922').forEach(el => this.#listen(el, '12922', 'click', () => { void runAndRefresh(this, item).catch((runError) => reportRunError(this, runError)); }));
    this.#eventTargets(scope, '13319').forEach(el => this.#listen(el, '13319', 'click', () => {
    void setAutomationEnabled(item.id, !item.enabled)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to update automation.";
    });
}));
    this.#eventTargets(scope, '13997').forEach(el => this.#listen(el, '13997', 'click', () => {
    if (!window.confirm(`Delete automation "${item.name}"?`))
        return;
    void deleteAutomation(item.id)
        .then(() => load(this))
        .catch(() => {
        this.error = "Failed to delete automation.";
    });
}));
    this.#eventTargets(scope, '16343').forEach(el => this.#listen(el, '16343', 'click', (event: Event) => {
    if (event.target === event.currentTarget)
        this.creating = false;
}));
    this.#eventTargets(scope, '16516').forEach(el => this.#listen(el, '16516', 'submit', (event: Event) => { submitCreateAutomation(this, event); }));
    this.#eventTargets(scope, '16853').forEach(el => this.#listen(el, '16853', 'click', () => { this.creating = false; }));
    this.#eventTargets(scope, '17117').forEach(el => this.#listen(el, '17117', 'change', (event: Event) => { this.createTrigger = (event.target as HTMLSelectElement).value; }));
    this.#eventTargets(scope, '19208').forEach(el => this.#listen(el, '19208', 'click', () => { this.creating = false; }));
    this.#eventTargets(scope, '19552').forEach(el => this.#listen(el, '19552', 'click', (event: Event) => {
    if (event.target === event.currentTarget)
        this.webhookOpen = false;
}));
    this.#eventTargets(scope, '19976').forEach(el => this.#listen(el, '19976', 'click', () => { this.webhookOpen = false; }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
